import React, { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SearchProvider } from '@/contexts/SearchContext';
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AuthRedirect } from "@/components/auth/AuthRedirect";

// Lazy load components for code splitting
const Index = lazy(() => import("./pages/Index"));

const Contact = lazy(() => import("./pages/Contact"));
const Payment = lazy(() => import("./pages/Payment"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const QuestionManagement = lazy(() => import("./pages/admin/QuestionManagement"));
const SessionManagement = lazy(() => import("./pages/admin/SessionManagement"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const Practice = lazy(() => import("./pages/Practice"));
const QuestionPractice = lazy(() => import("./pages/QuestionPractice"));
const ExamPractice = lazy(() => import("./pages/ExamPractice"));
const Exams = lazy(() => import("./pages/Exams"));
const Progress = lazy(() => import("./pages/Progress"));
const Settings = lazy(() => import("./pages/Settings"));
const Support = lazy(() => import("./pages/Support"));
const Devices = lazy(() => import("./pages/Devices"));
const DeviceVerification = lazy(() => import("./pages/DeviceVerification"));
const ExamResults = lazy(() => import("./pages/ExamResults"));
const HighlightSearchResults = lazy(() => import("./pages/HighlightSearchResults"));
const NotFound = lazy(() => import("./pages/NotFound"));
import { WebVitals } from '@/components/WebVitals';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const queryClient = new QueryClient();

const AppRoutes = () => {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <Routes>
        <Route path="/" element={<Index />} />

        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard" 
          element={
            <ProtectedRoute requiredRole="user">
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute requiredRole="admin">
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/questions" 
          element={
            <ProtectedRoute requiredRole="admin">
              <QuestionManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/sessions" 
          element={
            <ProtectedRoute requiredRole="admin">
              <SessionManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/practice" 
          element={
            <ProtectedRoute requiredRole="user">
              <Practice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/question-practice" 
          element={
            <ProtectedRoute requiredRole="user">
              <QuestionPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/exam-practice/:examId" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exam/:examId" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exam/category-practice" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exam/mock-exam" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exam/university-exam" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/exam-practice/:university" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamPractice />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/exams" 
          element={
            <ProtectedRoute requiredRole="user">
              <Exams />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/progress" 
          element={
            <ProtectedRoute requiredRole="user">
              <Progress />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/settings" 
          element={
            <ProtectedRoute requiredRole="user">
              <Settings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/support" 
          element={
            <ProtectedRoute requiredRole="user">
              <Support />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/devices" 
          element={
            <ProtectedRoute requiredRole="user">
              <Devices />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/highlights/search" 
          element={
            <ProtectedRoute requiredRole="user">
              <HighlightSearchResults />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exam/results/:id" 
          element={
            <ProtectedRoute requiredRole="user">
              <ExamResults />
            </ProtectedRoute>
          } 
        />
        <Route path="/device-verification" element={<DeviceVerification />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/auth-redirect" element={<AuthRedirect />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Analytics />
    <SearchProvider>
      <WebVitals />
      <AppRoutes />
    </SearchProvider>
  </QueryClientProvider>
);

export default App;
